import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { useTeams } from '@/hooks/useTeams';
import { useCreateTool, useUpdateTool } from '@/hooks/useTools';
import { toast } from 'sonner';
import type { Tables } from '@/integrations/supabase/types';

const CATEGORIES = ['Produtividade', 'Design', 'Desenvolvimento', 'Análise de Dados', 'Comunicação', 'Geral'];

const toolSchema = z.object({
  name: z.string().trim().min(2, 'Nome deve ter pelo menos 2 caracteres').max(100),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  url: z.string().trim().url('URL inválida'),
  category: z.string().trim().max(50).optional().or(z.literal('')),
  icon: z.string().trim().min(1).max(10).default('🔧'),
  scopeValue: z.string().min(1, 'Selecione um escopo'),
});

type ToolFormValues = z.infer<typeof toolSchema>;

function parseScopeValue(value: string) {
  if (value === 'global') return { scope_type: 'global', scope_id: null, project_id: null };
  if (value === 'personal') return { scope_type: 'personal', scope_id: null, project_id: null };
  if (value.startsWith('team:')) return { scope_type: 'team', scope_id: value.slice(5), project_id: null };
  if (value.startsWith('project:')) return { scope_type: 'global', scope_id: null, project_id: value.slice(8) };
  return { scope_type: 'global', scope_id: null, project_id: null };
}

function buildScopeValue(tool: Tables<'tools'>): string {
  if (tool.project_id) return `project:${tool.project_id}`;
  if (tool.scope_type === 'team' && tool.scope_id) return `team:${tool.scope_id}`;
  if (tool.scope_type === 'personal') return 'personal';
  return 'global';
}

interface ToolFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tool?: Tables<'tools'>;
}

export const ToolFormModal = ({ open, onOpenChange, tool }: ToolFormModalProps) => {
  const { user } = useAuth();
  const { navigation } = useNavigation();
  const { data: teams } = useTeams();
  const createTool = useCreateTool();
  const updateTool = useUpdateTool();
  
  const isEditing = !!tool;

  const defaultScopeValue = () => {
    if (navigation.projectId) return `project:${navigation.projectId}`;
    if (navigation.isPersonal) return 'personal';
    return 'global';
  };

  const form = useForm<ToolFormValues>({
    resolver: zodResolver(toolSchema),
    defaultValues: {
      name: '',
      description: '',
      url: '',
      category: 'Geral',
      icon: '🔧',
      scopeValue: defaultScopeValue(),
    },
  });

  useEffect(() => {
    if (open) {
      if (tool) {
        form.reset({
          name: tool.name,
          description: tool.description ?? '',
          url: tool.url,
          category: tool.category ?? 'Geral',
          icon: tool.icon ?? '🔧',
          scopeValue: buildScopeValue(tool),
        });
      } else {
        form.reset({
          name: '',
          description: '',
          url: '',
          category: 'Geral',
          icon: '🔧',
          scopeValue: defaultScopeValue(),
        });
      }
    }
  }, [open, tool]);

  const onSubmit = async (values: ToolFormValues) => {
    const { scope_type, scope_id, project_id } = parseScopeValue(values.scopeValue);

    try {
      if (isEditing) {
        await updateTool.mutateAsync({
          id: tool.id,
          name: values.name,
          description: values.description || null,
          url: values.url,
          category: values.category || null,
          icon: values.icon,
          scope_type,
          scope_id,
          project_id,
        });
        toast.success('Ferramenta atualizada');
      } else {
        await createTool.mutateAsync({
          name: values.name,
          description: values.description || null,
          url: values.url,
          category: values.category || null,
          icon: values.icon,
          scope_type,
          scope_id,
          project_id,
        });
        toast.success('Ferramenta criada com sucesso');
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(`Erro ao salvar ferramenta: ${(err as Error).message}`);
    }
  };

  const isPending = createTool.isPending || updateTool.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Ferramenta' : 'Nova Ferramenta'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Nome + Ícone */}
          <div className="flex gap-3">
            <div className="w-16">
              <label className="block text-sm font-medium text-primary mb-1.5">Ícone</label>
              <input
                {...form.register('icon')}
                className="w-full px-2 py-2 rounded-md border border-color bg-surface text-center text-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-primary mb-1.5">Nome *</label>
              <input
                {...form.register('name')}
                className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Nome da ferramenta"
              />
              {form.formState.errors.name && (
                <p className="text-xs text-destructive mt-1">{form.formState.errors.name.message}</p>
              )}
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-primary mb-1.5">Descrição</label>
            <textarea
              {...form.register('description')}
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
              placeholder="Descrição opcional"
            />
          </div>

          {/* URL */}
          <div>
            <label className="block text-sm font-medium text-primary mb-1.5">URL *</label>
            <input
              {...form.register('url')}
              type="url"
              className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder="https://exemplo.com"
            />
            {form.formState.errors.url && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.url.message}</p>
            )}
          </div>

          {/* Categoria + Escopo */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-primary mb-1.5">Categoria</label>
              <input
                {...form.register('category')}
                list="tool-categories"
                className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Selecione ou digite"
              />
              <datalist id="tool-categories">
                {CATEGORIES.map(c => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-primary mb-1.5">Escopo *</label>
              <select
                {...form.register('scopeValue')}
                className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                <option value="global">🏢 Espaço Caldeira</option>
                {teams?.map(team => (
                  <option key={team.id} value={`team:${team.id}`}>
                    {team.icon} Time — {team.name}
                  </option>
                ))}
                <option value="personal">👤 Pessoal</option>
                {navigation.projectId && (
                  <option value={`project:${navigation.projectId}`}>
                    📁 Projeto atual
                  </option>
                )}
              </select>
              {form.formState.errors.scopeValue && (
                <p className="text-xs text-destructive mt-1">{form.formState.errors.scopeValue.message}</p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 border border-color rounded-md text-sm font-medium text-primary hover:bg-hover"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 brand-primary rounded-md text-sm font-medium disabled:opacity-50"
            >
              {isPending ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar Ferramenta'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
